import { useCallback, useMemo, useState, type ChangeEvent } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { GridColDef } from "@mui/x-data-grid-pro";
import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";

import AddEditPhraseModal from "./AddEditPhraseModal";
import PhraseBodyTextCell from "./PhraseBodyTextCell";
import type { Phrase, UserPhraseCreateInput } from "./PhraseTextField";
import { filterPhrasesBySearch } from "./PhraseTextField.utils";

const getPhraseRowHeight = () => "auto" as const;

type PhraseRow = Phrase & { expanded: boolean; actionsDisabled: boolean };
type PhraseModalState = { mode: "add" } | { mode: "edit"; phrase: Phrase };

export type PhrasesCardProps = {
  title: string;
  phrases: ReadonlyArray<Phrase>;
  onCreatePhrase: (input: UserPhraseCreateInput) => Promise<boolean>;
  onUpdatePhrase: (input: UserPhraseCreateInput & { id: string }) => Promise<boolean>;
  onArchivePhrase: (phrase: Phrase) => Promise<void>;
  loading?: boolean;
  archiving?: boolean;
  saving?: boolean;
  readOnly?: boolean;
};

const PhrasesCard = ({
  title,
  phrases,
  onCreatePhrase,
  onUpdatePhrase,
  onArchivePhrase,
  loading = false,
  archiving = false,
  saving = false,
  readOnly = false,
}: PhrasesCardProps) => {
  const { t } = useTranslation(["common", "glossary"]);

  const [phraseModalState, setPhraseModalState] = useState<PhraseModalState | null>(null);
  const [expandedPhraseIds, setExpandedPhraseIds] = useState<ReadonlySet<string>>(new Set());
  const [searchText, setSearchText] = useState("");

  const handleSearchTextChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  }, []);

  const handleAddPhrase = useCallback(() => setPhraseModalState({ mode: "add" }), []);

  const handleEdit = useCallback((phrase: Phrase) => () => setPhraseModalState({ mode: "edit", phrase }), []);

  const handleArchive = useCallback((phrase: Phrase) => () => onArchivePhrase(phrase), [onArchivePhrase]);

  const togglePhraseExpanded = useCallback((phraseId: string) => {
    setExpandedPhraseIds((currentPhraseIds) => {
      const updatedPhraseIds = new Set(currentPhraseIds);
      if (updatedPhraseIds.has(phraseId)) updatedPhraseIds.delete(phraseId);
      else updatedPhraseIds.add(phraseId);
      return updatedPhraseIds;
    });
  }, []);

  const columns = useMemo<Array<GridColDef<PhraseRow>>>(
    () => [
      { field: "name", headerName: t("common:name"), flex: 1, minWidth: 110 },
      {
        field: "bodyText",
        headerName: t("common:body_text"),
        flex: 2,
        minWidth: 0,
        resizable: false,
        renderCell: ({ row }) => (
          <PhraseBodyTextCell phrase={row} expanded={row.expanded} onToggleExpansion={togglePhraseExpanded} />
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 40,
        minWidth: 40,
        resizable: false,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label={t("common:edit")}
            onClick={handleEdit(row)}
            disabled={row.actionsDisabled}
            showInMenu
          />,
          <GridActionsCellItem
            key="archive"
            icon={<DeleteIcon />}
            label={t("common:delete")}
            onClick={handleArchive(row)}
            disabled={row.actionsDisabled}
            showInMenu
          />,
        ],
      },
    ],
    [handleArchive, handleEdit, t, togglePhraseExpanded],
  );

  const rows = useMemo<Array<PhraseRow>>(
    () =>
      phrases.map((phrase) => ({
        ...phrase,
        expanded: expandedPhraseIds.has(phrase.id),
        /* This property is directly on the row because if columns directly referenced 'archiving', the
        columns would be recreated every time there was an archive, setting them back to default widths. */
        actionsDisabled: archiving || readOnly,
      })),
    [archiving, phrases, expandedPhraseIds, readOnly],
  );

  const filteredRows = useMemo(() => filterPhrasesBySearch(rows, searchText), [rows, searchText]);

  const hasSearchablePhrases = phrases.length > 0;

  const handleExpandAll = useCallback(
    () => setExpandedPhraseIds(new Set(rows.map((phrase) => phrase.id))),
    [rows],
  );

  const handleCollapseAll = useCallback(() => setExpandedPhraseIds(new Set()), []);

  const closePhraseModal = useCallback(() => setPhraseModalState(null), []);

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        maxHeight: { xs: "calc(100svh - 96px)", sm: "none" },
      }}
    >
      <Box
        flexShrink={0}
        gap={1}
        p={1}
        pt={0.5}
        sx={{
          display: "grid",
          alignItems: "center",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gridTemplateAreas: '"title actions" "search add"',
        }}
      >
        <Typography sx={{ gridArea: "title" }} variant="h2">
          {title}
        </Typography>
        <Box display="flex" sx={{ gridArea: "actions", justifySelf: "end" }}>
          <Tooltip title={t("common:collapse_all_body_text")} placement="top" arrow>
            <IconButton onClick={handleCollapseAll} aria-label={t("common:collapse_all_body_text")}>
              <UnfoldLessIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("common:expand_all_body_text")} placement="top" arrow>
            <IconButton onClick={handleExpandAll} aria-label={t("common:expand_all_body_text")}>
              <UnfoldMoreIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          hiddenLabel
          margin="none"
          placeholder={t("common:search_phrases")}
          size="small"
          value={searchText}
          onChange={handleSearchTextChange}
          sx={{ gridArea: "search" }}
          slotProps={{
            htmlInput: {
              "aria-label": t("common:search_phrases"),
              sx: { fontSize: "0.875rem", py: 0.5 },
            },
            input: {
              sx: { height: 32, pl: 1 },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Button
          size="small"
          variant="outlined"
          sx={{ gridArea: "add", height: 32, py: 0.5, whiteSpace: "nowrap" }}
          startIcon={<AddIcon />}
          onClick={handleAddPhrase}
          disabled={loading || archiving || readOnly}
        >
          {t("common:add_phrase")}
        </Button>
      </Box>
      <Divider />
      <Box sx={{ p: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box sx={{ minWidth: 0, minHeight: 0, maxHeight: 600, overflowX: "hidden", overflowY: "auto" }}>
          <DataGridPro<PhraseRow>
            autoHeight
            sx={{
              border: 1,
              borderRight: 0,
              borderColor: "divider",
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "hidden",
              },
              "& .MuiDataGrid-columnHeaders": {
                borderBottomWidth: 2,
              },
              '& .MuiDataGrid-cell[data-field="actions"]': {
                px: 1,
              },
            }}
            columns={columns}
            rows={filteredRows}
            initialState={{
              sorting: { sortModel: [{ field: "name", sort: "asc" }] },
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
            }}
            pagination
            paginationMode="client"
            pageSizeOptions={[10, 25, 50]}
            loading={loading}
            getRowHeight={getPhraseRowHeight}
            disableRowSelectionOnClick
            disableColumnSelector
            disableColumnReorder
            showCellVerticalBorder
            showColumnVerticalBorder
            localeText={{
              noRowsLabel: t(
                searchText.trim() && hasSearchablePhrases ? "common:no_phrases_match" : "common:no_phrases",
              ),
            }}
            slotProps={{
              noRowsOverlay: { sx: { typography: "body2", color: "text.secondary" } },
            }}
          />
        </Box>
        {phraseModalState && (
          <AddEditPhraseModal
            {...phraseModalState}
            phrases={rows}
            onCreatePhrase={onCreatePhrase}
            onUpdatePhrase={onUpdatePhrase}
            saving={saving}
            onClose={closePhraseModal}
          />
        )}
      </Box>
    </Paper>
  );
};

export default PhrasesCard;
